import React, { memo, useMemo, useState, useCallback } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { SelectOption } from "./types";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { SelectedValueBadge } from "./SelectedValueBadge";
import { useTranslation } from "react-i18next";

interface DropdownMultiSelectProps {
  placeholder?: string;
  options: SelectOption[];
  value: string[];
  onUpdate: (val: string[]) => void;
  allowSearch?: boolean;
  emptyResults?: string;
  disabled?: boolean;
}

export function DropdownMultiSelect({
  placeholder,
  value,
  onUpdate,
  options,
  allowSearch,
  emptyResults,
  disabled,
}: DropdownMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const labelMap = useMemo(() => {
    return options.reduce<Record<string, string>>((acc, cur) => {
      acc[cur.value] = cur.label;
      return acc;
    }, {});
  }, [options]);

  const onSelect = useCallback(
    (currentValue: string) => {
      if (value.includes(currentValue)) {
        onUpdate(value.filter((item) => item !== currentValue));
      } else {
        onUpdate([...value, currentValue]);
      }
      setOpen(false);
    },
    [value, onUpdate],
  );

  const onRemove = useCallback(
    (val: string) => onUpdate(value.filter((token) => token !== val)),
    [value, onUpdate],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger disabled={disabled} asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-9.5 w-full justify-between border-border-dark text-muted-foreground"
          disabled={disabled}
        >
          <div className="flex min-w-0 flex-row flex-wrap gap-1">
            {value.length === 0 && <span>{placeholder}</span>}
            {value.map((selectedValue) => {
              return (
                <SelectedValueBadge
                  key={selectedValue}
                  label={labelMap[selectedValue]}
                  value={selectedValue}
                  onRemove={onRemove}
                ></SelectedValueBadge>
              );
            })}
          </div>
          <ChevronsUpDown strokeWidth="3" className="text-input-icon" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        style={{ width: "var(--radix-popover-trigger-width)" }}
        className="p-0"
      >
        <Command tabIndex={allowSearch ? -1 : 0}>
          {allowSearch && (
            <CommandInput placeholder={placeholder} className="h-9" />
          )}
          <CommandList aria-multiselectable={true}>
            <CommandEmpty>{emptyResults}</CommandEmpty>
            <CommandGroup aria-label={t("global.all")}>
              {options.map((option) => (
                <CommandItemContent
                  key={option.value}
                  option={option}
                  value={value}
                  onSelect={onSelect}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const CommandItemContent = memo(function CommandItemContent({
  value,
  option,
  onSelect,
}: {
  value: string[];
  option: SelectOption;
  onSelect: (value: string) => void;
}) {
  return (
    <CommandItem value={option.label} onSelect={() => onSelect(option.value)}>
      {option.label}
      <Check
        className={cn(
          "ml-auto",
          value.includes(option.value) ? "opacity-100" : "opacity-0",
        )}
      />
    </CommandItem>
  );
});
